// eslint-disable-next-line import/no-unassigned-import
import { autoinject, bindable, BindingEngine, Disposable, TaskQueue } from 'aurelia-framework';
import { NavigationInstruction, RouteConfig, Router, RouterConfiguration, RouterEvent } from 'aurelia-router';
import { EventAggregator } from 'aurelia-event-aggregator';
import { CustomerSettings } from '../../classes/customer-settings';
import { CustomerSelector } from '../../classes/customer-selector';
import { CheckRouteAccessStep, isRouteAllowed } from '../../services/check-route-access-step';
import { UserContext } from '../../services/user-context';
import { customerSettingsRoute, routeConfigs } from './route-configs';
import { MdcDrawer } from '@aurelia-mdc-web/drawer';
import { AlertService } from '@aurelia-toolkit/alert';
import { usingTraceAwareProgress } from '@dynamik/common';
import { MdcFocusTrap } from '@aurelia-mdc-web/base';
import { Route } from './route';
import { IMenuItem } from './i-menu-item';
import { MdcMenu } from '@aurelia-mdc-web/menu';
import { closest } from '@material/dom/ponyfill';
import { TokenClient } from '@dynamik/common/clients/identity-clients';
import { Subject, debounceTime, distinctUntilChanged } from '../../rxjs-local';
import { TransactionCustomerCache } from '../../services/transaction-customer-cache';
import { MdcBanner } from '@aurelia-mdc-web/banner';
import { CustomerViewModel } from '@dynamik/common/clients/transaction-clients';
import { MyCustomerViewModel, OpaGetUserResultUser } from '@dynamik/common/clients/policy-clients';
import { CustomerService, InstrumentTypeValueConverter, when } from '@dynamik/common';
import { ThemeService } from '../../services/theme-service';
import { ForbiddenException } from '@dynamik/common';
import { AureliaContextPipelineStep } from './AureliaContextPipelineStep';
import { OtelNavigationInstrumentation } from './OtelNavigationInstrumentation';
import { NotificationService } from '../../services/notification/notification-service';
import { Notification, NotificationStepStatus } from '@dynamik/common/clients/notification-clients';
import { NotificationsList } from './notifications-list/notifications-list';
import { MdcSnackbarService } from '@aurelia-mdc-web/snackbar';

const MAIN_MENU_OPEN_KEY = 'mainMenuOpen';
const MAIN_MENU_ICONS_ONLY_KEY = 'mainMenuIconsOnly';

@autoinject
export class App {
  constructor(private eventAggregator: EventAggregator, private customerSettings: CustomerSettings, private bindingEngine: BindingEngine, private customerSelector: CustomerSelector,
    private userContext: UserContext, public alertService: AlertService, private notificationService: NotificationService,
    private taskQueue: TaskQueue, private tokenClient: TokenClient, public transactionCustomerCache: TransactionCustomerCache,
    private instrumentTypeValueConverter: InstrumentTypeValueConverter, private customerService: CustomerService, private themeService: ThemeService, private router: Router,
    private snackbarService: MdcSnackbarService) {
    this.eventAggregator.subscribe(RouterEvent.Complete, (event: { instruction: NavigationInstruction }) => this.showDashboardMenu = event.instruction.config.name === 'home');
  }

  subscription: Disposable;
  mainMenu: MdcDrawer;
  notificationsDrawer: MdcDrawer;
  categoryRoutesAllowed: boolean;
  busy: boolean;
  progressFocusTrap: MdcFocusTrap;
  progress: HTMLElement;
  progressNumber: number;
  progressText: string;
  notificationsDrawerOpen: boolean;
  mainMenuIconsOnly: boolean;
  menu: IMenuItem[];
  expandedMenuItem: IMenuItem = undefined;
  tokenExpiryDate$ = new Subject<Date>();
  banner: MdcBanner;
  bannerOpen: boolean;
  isEulaRoute: boolean;
  user: OpaGetUserResultUser;
  dashboardMenuAnchor: HTMLElement = undefined;
  dashboardMenu: MdcMenu;
  notifications: Notification[];
  notificationAlertCount: number | undefined;
  notificationsList: NotificationsList;

  @bindable()
  showDashboardMenu: boolean = true;

  async navigate(routeName: RouteConfig, model) {
    this.mainMenu.open = false;
    this.mainMenu.open = false;
    this.router.navigateToRoute(routeName.name, model);
  }

  async navigateCategory(route: RouteConfig) {
    this.mainMenu.open = false;
    this.router.navigateToRoute('categories', { typeId: route.CatTypeId });
  }

  async onMenuSelect(data: 'logout' | 'resetDashboard' | { action: string; type: string; typeId: number }) {
    switch (data) {
      case 'logout':
        this.customerSettings.logout();
        return;
      case 'resetDashboard':
        localStorage.removeItem('gridstackv2');
        return window.location.reload();
      default:
        if (data.action === 'addDashItem') {
          this.eventAggregator.publish('addDashBoardItem', data);
        }
    }
  }

  @usingTraceAwareProgress()
  async activate() {
    this.user = await this.userContext.get();
    if (!this.user) {
      this.alertService.error('You are not authorised to use this application. Please contact your administrator.');
      return;
    }
    this.isEulaRoute = location.pathname === `/${Route.acceptEula}`;
    if (this.isEulaRoute) {
      return;
    }
    try {
      await this.customerSelector.setCustomerDefaultValue();
      await this.refreshCategorySettings();
      await this.notificationService.start();
    } catch (e) {
      if (e instanceof ForbiddenException) {
        // ignored
      } else {
        throw e;
      }
    }
  }

  async attached() {
    // make progress background focusable so that focus trap could work
    this.progress.querySelector<HTMLElement>('.progress-background').tabIndex = 0;
    this.alertService.busy$
      .pipe(debounceTime(100), distinctUntilChanged())
      .subscribe(x => {
        if (x) {
          this.taskQueue.queueTask(() => {
            if (this.busy) {
              try {
                this.progressFocusTrap.trapFocus();
              } catch {
                // it is unclear why sometimes the focus trap element is not found, some binding timing issues
                // can be ignored
              }
            }
          });
        } else {
          this.progressFocusTrap.releaseFocus();
        }
        this.progressNumber = undefined;
        this.progressText = undefined;
        this.busy = x;
      });

    if (this.isEulaRoute) {
      return;
    }

    this.mainMenuIconsOnly = localStorage.getItem(MAIN_MENU_ICONS_ONLY_KEY) === 'true';
    await this.mainMenu.initialised;
    this.mainMenu.open = localStorage.getItem(MAIN_MENU_OPEN_KEY) === 'true';
    // update text and uncomment if needed
    // this.snackbarService.open('Due to maintenance Dynamik might have some down time from Monday 20:00', undefined, { dismissible: true, timeout: -1, classes: 'mdc-snackbar--warning mdc-snackbar--downtime' });
    this.notificationService.notifications$
      .subscribe(x => {
        this.notifications = x;
        const alertCount = x.filter(n => n.steps?.some(s => s.status === NotificationStepStatus.Error || s.status === NotificationStepStatus.Warning)).length;
        this.notificationAlertCount = alertCount || undefined;
      });
  }

  async bind() {
    this.subscription = this.bindingEngine.propertyObserverEx(this.customerSettings, 'selectedCustomer').subscribe(async () => {
      await this.refreshCategorySettings();
    });
  }

  async unbind() {
    this.subscription.dispose();
  }

  async refreshCategorySettings() {
    await this.customerSelector.customerIsSetPromise;
    await Promise.all([
      this.instrumentTypeValueConverter.init(),
      this.customerService.initCategories()
    ]);
    this.buildMenu();
  }

  get selectedCustomer() {
    return this.customerSettings.selectedCustomer;
  }

  set selectedCustomer(value: MyCustomerViewModel) {
    if (value) {
      this.customerSettings.selectedCustomer = value;
    }
  }

  toggleMainMenu() {
    this.mainMenu.open = !this.mainMenu.open;
    localStorage.setItem(MAIN_MENU_OPEN_KEY, this.mainMenu.open.toString());
  }

  async configureRouter(config: RouterConfiguration, router: Router) {
    config.title = 'Dynamik';
    config.options.pushState = true;
    config.options.root = '/';
    config.addPreActivateStep(AureliaContextPipelineStep);
    config.addPreActivateStep(OtelNavigationInstrumentation);
    config.addAuthorizeStep(CheckRouteAccessStep);
    config.map(routeConfigs);
    await this.buildMenu();
    this.router = router;
  }

  async buildMenu() {
    if (this.isEulaRoute) {
      return;
    }
    this.user = await this.userContext.get();
    if (!this.user) {
      return;
    }
    const customer = await this.transactionCustomerCache.get();
    const categoryTypesRouteAllowed = isRouteAllowed(customerSettingsRoute, this.user, this.customerSettings);
    this.menu = [
      { text: 'Documents', route: Route.documents, icon: 'email' },
      { text: 'Overview', route: Route.overview, icon: 'list' },
      { text: 'Processed vouchers', route: Route.processedVouchers, icon: 'inventory' },
      { text: 'Vouchers detailed', title: 'Processed vouchers detailed', route: Route.processedVouchersDetailed, icon: 'details' },
      { text: 'Transactions overview', route: Route.transactionsOverview, icon: 'receipt_long' },
      { text: 'Tax values', route: Route.taxValues, icon: 'event_available' },
      { text: 'Reports', route: Route.reports, icon: 'auto_stories' },
      { text: 'Reconcile', route: Route.reconcileNew, icon: 'fact_check' },
      ...when<IMenuItem>(categoryTypesRouteAllowed, { text: 'Categories', route: Route.categoryTypes, icon: 'category' }),
      ...when<IMenuItem>(await this.userContext.isAdmin() || await this.userContext.isCustomerAdmin(this.customerService.selectedCustomer.companyId),
        { text: 'Settings', route: Route.settings, icon: 'settings' },
      )
    ];
  }

  gotoSettings() {
    this.router.navigate(Route.customerSettings);
  }

  userChangedSub$ = this.notificationService.userChanged$.subscribe(async () => {
    await this.alertService.alert({ message: 'Your account details has been changed. Please login again.', icon: 'warning', iconColour: 'warning' });
    this.customerSettings.logout();
  });

  private autoOpenNotificationStatuses = [NotificationStepStatus.Error, NotificationStepStatus.Warning];
  clientNotificationSub$ = this.notificationService.clientNotification$.subscribe(cn => {
    if (cn.notification?.steps?.some(s => this.autoOpenNotificationStatuses.includes(s.status))
      || this.autoOpenNotificationStatuses.includes(cn.step?.status)) {
      // find the notification and expand it after a short delay to allow the list of notifications to refresh
      setTimeout(() => {
        const n = this.notificationsList.notifications.find(n => n.id === (cn.notification?.id || cn.step?.notificationId));
        if (n) {
          n.expanded = true;
          if (this.notificationsDrawer) {
            this.notificationsDrawer.open = true;
          }
        }
      }, 1000);
    }
  });

  updateProgress(progress: number | undefined, text: string | undefined) {
    this.progressNumber = progress;
    this.progressText = text;
  }

  handleNotificationsDrawerEvent() {
    this.notificationsDrawerOpen = this.notificationsDrawer.open;
  }

  toggleMainMenuIconsOnly() {
    this.mainMenuIconsOnly = !this.mainMenuIconsOnly;
    localStorage.setItem(MAIN_MENU_ICONS_ONLY_KEY, this.mainMenuIconsOnly.toString());
  }

  toggleRootMenuItem(mi: IMenuItem) {
    this.expandedMenuItem = this.expandedMenuItem === mi ? undefined : mi;
    // navigate to first child only if root is expanded and active route is not amongst children
    if (mi.navigateToFirstOnExpand && this.expandedMenuItem && !mi.children.find(x => x.route === this.router.currentInstruction.config.name)) {
      const first = mi.children[0];
      this.router.navigateToRoute(first.route, first.routeParams);
    }
  }

  handleMenuItemPointerEnter(mi: IMenuItem, subMenu: MdcMenu) {
    if (mi === this.expandedMenuItem) {
      return;
    }
    subMenu.open = true;
  }

  handleMenuItemPointerLeave(mi: IMenuItem, subMenu: MdcMenu, evt: PointerEvent) {
    if (!evt.relatedTarget || closest(evt.relatedTarget as Element, 'mdc-menu') !== subMenu.root) {
      subMenu.open = false;
    }
  }

  handleSubMenuPointerLeave(rootMenuItem: HTMLElement, subMenu: MdcMenu, evt: PointerEvent) {
    if (!evt.relatedTarget || closest(evt.relatedTarget as Element, 'mdc-deprecated-list-item') !== rootMenuItem) {
      subMenu.open = false;
    }
  }

  bannerClosed() {
    this.bannerOpen = false;
  }

  @usingTraceAwareProgress()
  async getExcelToken() {
    const token = await this.tokenClient.excelAccessToken();
    // copy token to clipboard
    navigator.clipboard.writeText(token);
    this.alertService.alert({ message: 'Excel token copied to clipboard', icon: 'info', iconColour: 'mdc-theme--primary' });
  }

  async loadTheme(dark: boolean) {
    await this.themeService.loadTheme(dark);
  }
}
